/* eslint-disable max-classes-per-file */
import { fetch } from '@lmn/lmn-fetch';

export type JSONDocument = string | number | boolean | null | { [x: string]: JSONDocument } | Array<JSONDocument>;

type Options = Omit<RequestInit, 'body'> & {
  body?: JSONDocument;
};

export type FetcherParams = {
  url: string;
  name: string;
  options?: Options;
};

export type FetcherError<BadRequestFetcherErrorResponse = Response> =
  | UnauthorizedFetcherError
  | ForbiddenFetcherError
  | NotFoundFetcherError
  | BadRequestFetcherError<BadRequestFetcherErrorResponse>
  | Error;

export type FetcherResponse<JsonResponse> = Omit<Response, 'json'> & {
  json(): Promise<JsonResponse>;
};

export class UnauthorizedFetcherError extends Error {
  name = 'UnauthorizedFetcherError';

  constructor(name: string) {
    super(`${name} fetch failed with status 401`);
  }
}

export class ForbiddenFetcherError extends Error {
  name = 'ForbiddenFetcherError';

  constructor(name: string) {
    super(`${name} fetch failed with status 403`);
  }
}

export class NotFoundFetcherError extends Error {
  name = 'NotFoundFetcherError';

  constructor(name: string) {
    super(`${name} fetch failed with status 404`);
  }
}

export class BadRequestFetcherError<JsonResponse = Response> extends Error {
  name = 'BadRequestFetcherError';

  response: FetcherResponse<JsonResponse>;

  constructor(name: string, fetcherResponse: FetcherResponse<JsonResponse>) {
    super(`${name} fetch failed with status 400`);
    this.response = fetcherResponse;
  }
}

export const createFetcher =
  <JsonResponse = void, BadRequestFetcherErrorResponse = Response>(params: FetcherParams) =>
  async (): Promise<FetcherResponse<JsonResponse> | never> => {
    const { name, url, options = {} } = params;
    const { body, headers, ...otherOptions } = options;

    const response = await fetch(url, {
      ...otherOptions,
      body: body ? JSON.stringify(body) : undefined,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers,
      },
      tracing: {
        appName: 'cia-ui',
        methodName: name,
      },
    });
    if (response.status === 400) {
      throw new BadRequestFetcherError<BadRequestFetcherErrorResponse>(name, response);
    }
    if (response.status === 401) {
      throw new UnauthorizedFetcherError(name);
    }
    if (response.status === 404) {
      throw new NotFoundFetcherError(name);
    }
    if (response.status >= 400) {
      throw Error(`${name} fetch failed with status ${response.status}`);
    }
    return response;
  };
